import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '../../store/slices/limitsSlice';
import { LimitNotification } from '../../types/limits';
import { 
  BellIcon, 
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';

interface NotificationBellProps {
  orgId?: number;
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ orgId, className = '' }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { notifications, unreadNotificationCount, loading } = useAppSelector((state) => state.limits);
  
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const effectiveOrgId = orgId || user?.organization_id;

  // Fetch notifications on mount and set up polling
  useEffect(() => {
    if (effectiveOrgId) {
      dispatch(fetchNotifications({ orgId: effectiveOrgId, unreadOnly: false }));
      
      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        dispatch(fetchNotifications({ orgId: effectiveOrgId, unreadOnly: false }));
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [dispatch, effectiveOrgId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification: LimitNotification) => {
    if (!notification.read) {
      dispatch(markNotificationRead(notification.id));
    }
  };

  const handleMarkAllRead = () => {
    if (effectiveOrgId) {
      dispatch(markAllNotificationsRead(effectiveOrgId));
    }
  };

  const getNotificationIcon = (notification: LimitNotification) => {
    switch (notification.severity) {
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'info':
      default:
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
    }
  };

  const getNotificationBgColor = (notification: LimitNotification) => {
    if (!notification.read) {
      switch (notification.severity) {
        case 'error':
          return 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800';
        case 'warning':
          return 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800';
        case 'success':
          return 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800';
        case 'info':
        default:
          return 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800';
      }
    }
    return 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700';
  };

  const displayedNotifications = showAll ? notifications : notifications.slice(0, 5);
  const hasUnread = unreadNotificationCount > 0;
  const hasCritical = notifications.some(n => !n.read && (n.severity === 'error' || n.severity === 'warning'));

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors duration-200 ${
          hasUnread 
            ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30' 
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
        title={`${unreadNotificationCount} unread notifications`}
      >
        {hasUnread ? (
          <BellIconSolid className="w-6 h-6" />
        ) : (
          <BellIcon className="w-6 h-6" />
        )}
        
        {/* Notification Badge */}
        {unreadNotificationCount > 0 && (
          <span className={`absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-xs font-bold text-white rounded-full ${
            hasCritical ? 'bg-red-500' : 'bg-primary-500'
          }`}>
            {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-light-border dark:border-dark-border z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-light-border dark:border-dark-border">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadNotificationCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {loading.notifications ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-gray-500 dark:text-gray-400">
                <BellIcon className="w-8 h-8 mb-2" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <>
                {displayedNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 border-b border-light-border dark:border-dark-border cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                      !notification.read ? 'border-l-4' : ''
                    } ${getNotificationBgColor(notification)}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <h4 className={`text-sm font-medium ${
                            !notification.read 
                              ? 'text-gray-900 dark:text-white' 
                              : 'text-gray-700 dark:text-gray-300'
                          }`}>
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0 ml-2 mt-1"></div>
                          )}
                        </div>
                        
                        <p className={`text-sm mt-1 ${
                          !notification.read 
                            ? 'text-gray-700 dark:text-gray-300' 
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </span>
                          
                          {notification.notification_type && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              notification.severity === 'error' ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
                              notification.severity === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300' :
                              notification.severity === 'success' ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300' :
                              'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                            }`}>
                              {notification.notification_type.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Show More/Less Button */}
                {notifications.length > 5 && (
                  <div className="p-3 border-t border-light-border dark:border-dark-border">
                    <button
                      onClick={() => setShowAll(!showAll)}
                      className="w-full text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                    >
                      {showAll ? 'Show less' : `Show ${notifications.length - 5} more`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
